import { useState } from 'react';
import classNames from 'classnames';

import config from 'config';
import SearchIcon from 'assets/icons/search.svg';
import InputSearch from '@components/InputSearch';
import RestItemsCollapsible from '@components/RestItemsCollapsible';
import { MappedFilter } from '@components/SearchController/SearchController.utils';
import toKebabCase from 'utils/toKebabCase';
import parseHtmlEntities from 'utils/parseHtmlEntities';
import { useIntl } from 'utils/intl';

import Node from './components/Node';
import { Props } from './FilterTree.types';
import classes from './FilterTree.module.scss';
import messages from './FilterTree.messages';

const { acceptableFilterNodesLimit, quickFiltersSearchNodesLimit } = config;

const FilterTree = ({ nodes, mainLevelNodesLimit, getFilterChangeHref, checkedNodes }: Props): JSX.Element => {
  const [searchPhrase, setSearchPhrase] = useState('');
  const intl = useIntl();

  const filteredNodes = nodes.filter(({ name }) => name.toLowerCase().includes(searchPhrase.toLowerCase()));
  let limitedNodes = filteredNodes;
  let restNodes: MappedFilter['values'] = [];
  let restNodesCount = 0;
  if (mainLevelNodesLimit > 0 && filteredNodes.length > acceptableFilterNodesLimit + 1) {
    limitedNodes = filteredNodes.slice(0, mainLevelNodesLimit);
    restNodes = filteredNodes.slice(mainLevelNodesLimit);
    restNodesCount = restNodes.length;
  }

  const areRestNodesExpanded = Array.isArray(checkedNodes) && restNodes.some(({ id }) => checkedNodes.includes(id));

  const renderNodes = (subnodes: MappedFilter['values']) =>
    subnodes.map((item) => {
      const inputId = toKebabCase(`${item.searchKey}-${item.id}`);
      const isChecked = Array.isArray(checkedNodes) && checkedNodes.includes(item.id);
      const isTag = item.searchKey === 'tags';
      return (
        <Node
          key={inputId}
          id={item.id}
          inputId={inputId}
          classes={{ checkmark: isTag ? classes.tagCheckbox : undefined }}
          searchKey={item.searchKey}
          disabled={!item.count}
          activeIds={checkedNodes}
          trigger={
            <div className={classNames(classes.label, { [classes.active]: isChecked, [classes.tag]: isTag })}>
              {parseHtmlEntities(item.name)}{' '}
              <span className={classes.count} aria-disabled="true">{`(${item.count})`}</span>
            </div>
          }
          getFilterChangeHref={getFilterChangeHref}
        />
      );
    });

  return (
    <div className={classes.wrapper}>
      {nodes.length > quickFiltersSearchNodesLimit && (
        <div className={classes.quickSearch}>
          <SearchIcon width={16} />
          <InputSearch
            value={searchPhrase}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            onChange={setSearchPhrase}
            clearable
          />
        </div>
      )}
      {renderNodes(limitedNodes)}
      {!!restNodesCount && (
        <RestItemsCollapsible isInitiallyExpanded={areRestNodesExpanded} itemsCount={restNodesCount}>
          {renderNodes(restNodes)}
        </RestItemsCollapsible>
      )}
    </div>
  );
};

export default FilterTree;
